import type * as Sentry from '@sentry/react';
import type {
  AxiosInstance,
  AxiosResponse,
  AxiosStatic,
  CancelTokenSource,
} from 'axios';

import {CLOUDINARY_VIDEO_URL_PREFIX} from './media.ts';

export * from './media.ts';

// We assume the user is on a device with a device pixel ratio (DPR) of 2.0
// If we start using the Cloudinary SDK, we can get the DPR automatically
// NOTE: the DPR value is also hardcoded in UploadImageModal.tsx. If we change it here, we must update it there too.
export const CANCELLED_COMPONENT_UNMOUNTED = 'CANCELLED_COMPONENT_UNMOUNTED';
const CLOUDINARY_UPLOAD_URL = 'https://api.cloudinary.com/v1_1/edume/upload';
const EDUME_CLOUDINARY_UPLOAD_URL = '/api/cloudinary/uploadMedia';
export const CHUNK_SIZE_IN_BYTES = 5242880; // 5MB

type UploadSignatureResponse = {
  apiKey: string;
  timestamp: string;
  signature: string;
};

type CloudinaryResponse = {
  secure_url: string;
  original_filename: string;
  format: string;
  width?: number;
  height?: number;
  duration?: number;
};

// Checkers if a file is too big, so we can warn/error
export const shouldWarnFileTooBig = (
  file: File,
  type: 'video' | 'image'
): boolean => {
  let fileSize = file.size / 1048576; // 1024 * 1024
  fileSize = Math.round(fileSize * 10) / 10; // round to 1 decimal place
  return (
    (type === 'video' && fileSize > 40) || (type === 'image' && fileSize > 2)
  );
};

export const shouldErrorFileTooBig = (
  file: File,
  type: 'video' | 'image'
): boolean => {
  let fileSize = file.size / 1048576; // 1024 * 1024
  fileSize = Math.round(fileSize * 10) / 10; // round to 1 decimal place
  return (
    (type === 'video' && fileSize > 2048) || (type === 'image' && fileSize > 10)
  );
};

export const handleImageUpload = (
  client: AxiosInstance,
  file: File,
  config: MediaUploadConfig,
  axiosInstance: AxiosStatic,
  cancelTokenSource?: CancelTokenSource,
  logger?: typeof Sentry
) =>
  handleMediaUpload(
    client,
    file,
    config,
    axiosInstance,
    undefined,
    cancelTokenSource,
    logger
  );

export const handleVideoUpload = (
  client: AxiosInstance,
  file: File,
  config: MediaUploadConfig,
  axiosInstance: AxiosStatic,
  onChunkedUploadProgress?: (total: number, current: number) => void,
  cancelTokenSource?: CancelTokenSource,
  logger?: typeof Sentry
) =>
  handleMediaUpload(
    client,
    file,
    config,
    axiosInstance,
    onChunkedUploadProgress,
    cancelTokenSource,
    logger
  )
    //If its a cloudinary video, strip out the cloudinary part of the URL to match BE response structure (+ strip out width/height, unused)
    .then(({fileName, url, duration}) => ({
      fileName: fileName,
      url: url.split(CLOUDINARY_VIDEO_URL_PREFIX)[1],
      duration,
    }));

const createFormData = ({
  file,
  uploadPreset,
  apiKey,
  timestamp,
  signature,
}: {
  file: File | Blob;
  uploadPreset: string;
  apiKey: string;
  timestamp: string;
  signature: string;
}): FormData => {
  const data = new FormData();
  data.append('upload_preset', uploadPreset);
  data.append('api_key', apiKey);
  data.append('timestamp', timestamp);
  data.append('signature', signature);
  data.append('resource_type', 'auto');
  data.append('file', file);

  return data;
};

const getChunkData = (file: File, chunkIndex: number) => {
  const start = chunkIndex * CHUNK_SIZE_IN_BYTES;
  const end = Math.min(start + CHUNK_SIZE_IN_BYTES, file.size);
  return {
    chunk: file.slice(start, end),
    start,
    end,
  };
};

const handleMediaUploadError = (error: Error): never => {
  if (error.message !== CANCELLED_COMPONENT_UNMOUNTED) {
    // ignore requests cancelled because component doing the upload was unmounted
    console.error(error.message);
  }
  throw error;
};

type BaseConfig = {
  cloudinaryUploadPreset: string;
};

type ConfigWithoutIds = BaseConfig;

type ConfigWithPublicUploadCode = BaseConfig & {
  publicLinkCode: string;
  groupId: number;
};

type MediaUploadConfig = ConfigWithoutIds | ConfigWithPublicUploadCode;

const handleMediaUpload = async (
  client: AxiosInstance,
  file: File,
  config: MediaUploadConfig,
  axiosInstance: AxiosStatic,
  onChunkedUploadProgress?: (total: number, current: number) => void,
  cancelTokenSource?: CancelTokenSource,
  logger?: typeof Sentry
) => {
  const params = new URLSearchParams({
    preset: config.cloudinaryUploadPreset,
    ...('groupId' in config && {groupId: config.groupId.toString()}),
    ...('publicLinkCode' in config && {code: config.publicLinkCode}),
  });

  logger?.addBreadcrumb({
    category: 'media-upload',
    message: 'Starting media upload',
    data: {
      file: JSON.stringify(file, null, 2),
      uploadConfig: JSON.stringify(config, null, 2),
      isChunked: file.size > CHUNK_SIZE_IN_BYTES,
    },
    level: 'info',
  });

  const signaturePayload: AxiosResponse<UploadSignatureResponse> =
    await client.get(`/cloudinary/signUpload?${params}`);

  const {apiKey, timestamp, signature} = signaturePayload.data;

  logger?.addBreadcrumb({
    category: 'media-upload',
    message: 'Signature received',
    level: 'info',
  });

  if (file.size > CHUNK_SIZE_IN_BYTES) {
    return handleChunkedUpload(
      file,
      {
        apiKey,
        timestamp,
        signature,
        cancelTokenSource,
      },
      config,
      axiosInstance,
      onChunkedUploadProgress,
      logger
    );
  }

  // Stick to regular upload for smaller files
  const data = createFormData({
    file,
    uploadPreset: config.cloudinaryUploadPreset,
    apiKey,
    timestamp,
    signature,
  });

  logger?.addBreadcrumb({
    category: 'media-upload',
    message: 'Form data created',
    level: 'info',
  });

  const axiosConfig = cancelTokenSource
    ? {cancelToken: cancelTokenSource.token}
    : {};

  try {
    const uploadResponse = await axiosInstance.post(
      CLOUDINARY_UPLOAD_URL,
      data,
      axiosConfig
    );

    logger?.addBreadcrumb({
      category: 'media-upload',
      message: 'Upload response received',
      level: 'info',
    });

    if (!uploadResponse) {
      throw new Error('Upload failed - no response received');
    }

    return handleUploadResponse(uploadResponse);
  } catch (e) {
    logger?.addBreadcrumb({
      category: 'media-upload',
      message: 'Upload failed',
      data: {
        error: e,
        stringifiedError: JSON.stringify(e, null, 2),
      },
      level: 'error',
    });

    try {
      logger?.addBreadcrumb({
        category: 'media-upload',
        message:
          'Upload failed - trying upload through /api/cloudinary/uploadMedia',
        data: {
          error: e,
          stringifiedError: JSON.stringify(e, null, 2),
        },
        level: 'error',
      });

      const uploadResponse = await handleUploadThroughEduMeApi(
        file,
        config.cloudinaryUploadPreset,
        axiosInstance,
        cancelTokenSource
      );

      return handleUploadResponse(uploadResponse);
    } catch (err) {
      logger?.addBreadcrumb({
        category: 'media-upload',
        message: 'Upload failed after using /api/cloudinary/uploadMedia',
        data: {
          error: err,
          stringifiedError: JSON.stringify(err, null, 2),
        },
        level: 'error',
      });
      return handleMediaUploadError(err as Error);
    }
  }
};

const handleChunkedUpload = async (
  file: File,
  {
    apiKey,
    timestamp,
    signature,
    cancelTokenSource,
  }: {
    apiKey: string;
    timestamp: string;
    signature: string;
    cancelTokenSource?: CancelTokenSource;
  },
  config: {
    cloudinaryUploadPreset: string;
  },
  axiosInstance: AxiosStatic,
  onChunkedUploadProgress?: (total: number, current: number) => void,
  logger?: typeof Sentry
) => {
  const chunks = Math.ceil(file.size / CHUNK_SIZE_IN_BYTES);
  const uploadId = `${encodeURI(file.name)}-${timestamp}`;
  let uploadResponse;

  try {
    for (let i = 0; i < chunks; i++) {
      const {chunk, start, end} = getChunkData(file, i);

      if (onChunkedUploadProgress) {
        onChunkedUploadProgress(chunks, i + 1);
      }

      const data = createFormData({
        file: chunk,
        uploadPreset: config.cloudinaryUploadPreset,
        apiKey,
        timestamp,
        signature,
      });

      data.append('chunk_size', CHUNK_SIZE_IN_BYTES.toString());
      data.append('chunk_number', (i + 1).toString());
      data.append('total_chunks', chunks.toString());

      const axiosConfig = {
        ...(cancelTokenSource && {cancelToken: cancelTokenSource.token}),
        headers: {
          'Content-Range': `bytes ${start}-${end - 1}/${file.size}`,
          'X-Unique-Upload-Id': uploadId,
        },
      };

      uploadResponse = await axiosInstance.post(
        CLOUDINARY_UPLOAD_URL,
        data,
        axiosConfig
      );

      if (uploadResponse.status !== 200) {
        throw new Error('Chunk upload failed.');
      }
    }

    if (!uploadResponse) {
      throw new Error('Upload failed - no response received');
    }

    // when uploading in chunks the original filename gets overridden with 'blob'
    // so we need to set it back
    const format = uploadResponse.data.format;
    const baseFilename = file.name.replace(new RegExp(`\.${format}$`), '');

    const response = {
      ...uploadResponse,
      data: {
        ...uploadResponse.data,
        original_filename:
          baseFilename ?? uploadResponse.data.original_filename,
      },
    };

    return handleUploadResponse(response);
  } catch (e) {
    try {
      logger?.addBreadcrumb({
        category: 'media-upload',
        message:
          'Chunked upload failed - trying upload through /api/cloudinary/uploadMedia',
        data: {
          error: e,
          stringifiedError: JSON.stringify(e, null, 2),
        },
        level: 'error',
      });

      const edumeUploadResponse = await handleUploadThroughEduMeApi(
        file,
        config.cloudinaryUploadPreset,
        axiosInstance,
        cancelTokenSource
      );

      return handleUploadResponse(edumeUploadResponse);
    } catch (err) {
      logger?.addBreadcrumb({
        category: 'media-upload',
        message:
          'Chunked upload failed after using /api/cloudinary/uploadMedia',
        data: {
          error: err,
          stringifiedError: JSON.stringify(err, null, 2),
        },
        level: 'error',
      });
      return handleMediaUploadError(err as Error);
    }
  }
};

const handleUploadResponse = (response: AxiosResponse<CloudinaryResponse>) => {
  const {secure_url, original_filename, format, width, height, duration} =
    response.data;

  if (secure_url !== '') {
    const uploadFilename = original_filename + '.' + format;
    return {
      fileName: uploadFilename,
      url: secure_url,
      duration,
      width,
      height,
    };
  } else {
    throw new Error('Invalid image/video returned');
  }
};

const handleUploadThroughEduMeApi = async (
  file: File,
  preset: string,
  axiosInstance: AxiosStatic,
  cancelTokenSource?: CancelTokenSource
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('preset', preset);

  const axiosConfig = cancelTokenSource
    ? {cancelToken: cancelTokenSource.token}
    : {};

  const uploadResponse = await axiosInstance.post(
    EDUME_CLOUDINARY_UPLOAD_URL,
    formData,
    axiosConfig
  );

  return uploadResponse;
};
